import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: string | number;
  /** Label shown beside the value when rows stack as cards on phones (defaults to a string header). */
  label?: string;
}

/** Reusable, accessible table. Rows are keyed by `rowKey`. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty = 'Nothing here yet.',
  caption,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  empty?: ReactNode;
  caption?: string;
}) {
  if (!rows) return <div className="empty">Loading…</div>;
  return (
    <div className="table-wrap responsive">
      <table>
        {caption ? (
          <caption className="muted" style={{ textAlign: 'left', paddingBottom: 6 }}>
            {caption}
          </caption>
        ) : null}
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={c.width ? { width: c.width } : undefined} scope="col">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="empty">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={c.label ?? (typeof c.header === 'string' ? c.header : undefined)}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
